import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormOptional } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

const representativeFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Full name is required.')
    .transform((val) => val.trim()),
  email: z.string().min(1, 'Email address is required.').email('Invalid email format.'),
  phone: z
    .string()
    .optional()
    .transform((val) => val?.trim()),
  signatory: z.boolean().optional(),
});

export type IRepresentativeForm = z.infer<typeof representativeFormSchema>;

type Props = {
  onSubmit: (values: IRepresentativeForm) => void;
  onCancel: () => void;
  initialValues?: IRepresentativeForm;
  withSignatory?: boolean;
};

const FormContent = ({
  form,
  withSignatory = true,
}: {
  form: UseFormReturn<IRepresentativeForm>;
  withSignatory?: boolean;
}) => (
  <div className="space-y-4 pb-6">
    <FormField
      control={form.control}
      name="name"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Name</FormLabel>
          <FormControl>
            <Input {...field} placeholder="e.g. Jane Doe" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="email"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input {...field} placeholder="e.g. jane@company.com" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="phone"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between gap-2 mb-2">
            <FormLabel className="mb-0">Phone number</FormLabel>
            <FormOptional />
          </div>
          <FormControl>
            <Input {...field} placeholder="e.g. +1 415 555 0192" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />

    {withSignatory && (
      <FormField
        control={form.control}
        name="signatory"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start gap-2">
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="text-sm font-normal text-foreground">
                Is a signatory on the escrow agreement
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    )}
  </div>
);

const RepresentativeForm = ({ onSubmit, onCancel, initialValues, withSignatory = true }: Props) => {
  const form = useForm<IRepresentativeForm>({
    resolver: zodResolver(representativeFormSchema),
    defaultValues: initialValues,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormContent form={form} withSignatory={withSignatory} />

        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={form.formState.isSubmitting} disabled={form.formState.isSubmitting}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default RepresentativeForm;
